package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.data.dto.SportDto
import com.arena_web.core.viewmodel.LoginViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.dropdown.SportDropdownPanel
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mTop
import com.arena_web.ui.utils.mWidth
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

enum class MobileHeaderAction { Notification, Likes, Activity }

// What occupies the trailing (right-most) slot of the top bar. The profile
// avatar now lives ONLY on the Profile page; Arena swaps it for a chat
// shortcut that routes to /chat; every other page leaves the slot empty.
enum class MobileHeaderTrailing { None, Profile, Chat }

@Composable
fun MobileTopBar(
    onSportChanged: (String) -> Unit = {},
    // When non-null, the center pill is replaced by the page title text
    // (Figma 8285:172272 — Urbanist Medium 24/32 #B4CCF6).
    pageTitle: String? = null,
    // Selects the icon + badge tint for the right-side action button.
    // Notification = bell + primary badge (Figma 8398:85395); Likes = heart
    // + red badge (Figma 8285:375086, used on the People page); Activity =
    // pulse + primary badge (used on the Arena page instead of notifications).
    headerAction: MobileHeaderAction = MobileHeaderAction.Notification,
    // Trailing slot content — see [MobileHeaderTrailing]. Defaults to empty:
    // only the Profile page shows the avatar, only Arena shows the chat icon.
    trailing: MobileHeaderTrailing = MobileHeaderTrailing.None,
    // Replaces the brand logo on the left with a back arrow — used by pages
    // that left the bottom nav (Chat) and behave like drill-in surfaces.
    showBack: Boolean = false,
    // Custom widget for the right-side action slot — REPLACES the
    // notification/likes/activity button entirely (News mounts its
    // news↔matches mode toggle here). For guests it replaces the
    // Google/Apple sign-in buttons instead — there's no room for both.
    actionSlot: (@Composable () -> Unit)? = null,
    // Show the light/dark theme button in the right group (News only for now).
    showThemeButton: Boolean = false,
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current

    var isLoggedIn by remember { mutableStateOf(Storage.contains(StorageKeys.ACCESS_TOKEN)) }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    // The profile sheet hosts the avatar upload — refresh the trigger avatar
    // when a new one lands.
    DisposableEffect(Unit) {
        val avatarListener: (org.w3c.dom.events.Event) -> Unit = {
            avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
        }
        window.addEventListener("avatar-updated", avatarListener)
        onDispose { window.removeEventListener("avatar-updated", avatarListener) }
    }

    // Sports — read straight off the container's StateFlow so the cached
    // list (hydrated from localStorage by `ensureSportsLoaded`) is visible
    // as soon as the screen mounts. No need to wait for dropdown open.
    val sportsList by container.sportsList.collectAsState()
    var selectedSportName by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: "All")
    }
    var selectedOtherSport by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_OTHER_SPORT)?.takeIf { it.isNotBlank() })
    }
    var showSportDropdown by remember { mutableStateOf(false) }

    // Notifications (shown as badge on profile avatar) — driven by the
    // container's StateFlow that AppContainer keeps in sync via the global
    // user WS subscription.
    val notificationCount by container.unreadNotificationCount.collectAsState()
    // Active events/bookings count — badge for the Activity header action.
    val activityCount by container.activityCount.collectAsState()

    // Header bottom sheets — profile menu (avatar tap) + the right-side action.
    var showProfileSheet by remember { mutableStateOf(false) }
    var showNotificationsSheet by remember { mutableStateOf(false) }
    var showActivitySheet by remember { mutableStateOf(false) }

    // Full-screen post-detail sheet opened by tapping the heart action (Likes).
    var showLikesSheet by remember { mutableStateOf(false) }

    // Kick off the SWR fetch once on mount — the cache hydrates the
    // StateFlow synchronously inside `ensureSportsLoaded`, so the dropdown
    // is already populated by the time the user taps the pill.
    LaunchedEffect(Unit) {
        container.ensureSportsLoaded()
    }

    // Notifications count is owned by AppContainer's WS-driven collector;
    // the badge above just reads its StateFlow — no LaunchedEffect needed
    // here. Logout-time reset is also handled in `clearAuthenticatedClients`.

    // Outer Box gives the sport selector an absolute-positioning anchor so it
    // sits at the EXACT viewport horizontal center. Logo + right group flow
    // underneath with SpaceBetween, hugging the edges.
    Box(
        // .arena-header: light theme darkens the pill bases + glyphs inside
        // this subtree (AppTheme rules) — same treatment as the desktop bar.
        modifier = Modifier
            .fillMaxWidth()
            .mHeight(44)
            .classNames("arena-header")
            .styleModifier {
                property("position", "relative")
                property("flex-shrink", "0")
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left: back arrow (drill-in pages like Chat) or the Pro Play ring
            // emblem (no "Arena" wordmark on mobile per Figma 10032:208772).
            // ProPlayLogo SVG ships with preserveAspectRatio="none", so
            // width/height must match its natural 1.47:1 aspect or the ring
            // distorts. Shown immediately — no intro reveal / hero-logo fly-in.
            if (showBack) {
                Box(
                    modifier = Modifier
                        .mSize(36)
                        .mBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            // Deep-link fallback: with no in-app history the
                            // back arrow lands on Arena instead of doing nothing.
                            if (window.history.length > 1) window.history.back()
                            else ctx.router.navigateTo("/arena")
                        }
                        .styleModifier {
                            property("border", "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                    "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                        },
                    contentAlignment = Alignment.Center
                ) {
                    // ic_arrow_left.svg is actually a right-pointing chevron
                    // (identical to ic_arrow_right.svg); ChevronBack is the
                    // project's real back glyph — same one every mobile
                    // detail screen uses.
                    Icon(src = AppIcons.ChevronBack, modifier = Modifier.mSize(16))
                }
            } else {
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo("/") },
                    contentAlignment = Alignment.Center
                ) {
                    Img(
                        src = AppIcons.ProPlayLogo,
                        alt = "Pro Play Arena",
                        attrs = Modifier
                            .mWidth(46)
                            .mHeight(31)
                            .toAttrs()
                    )
                }
            }

            // Right: action button + trailing slot (logged in) or Auth buttons.
            if (isLoggedIn) {
                // Long sport names (Basketball / Volleyball) push the centred
                // Sport pill close to the right group — collapse to the tight
                // 4 px gap so the pill has room. Other sports get a slightly
                // wider gap so Notification floats a bit left of Profile.
                val isLongSport = selectedSportName.equals("Basketball", ignoreCase = true) ||
                    selectedSportName.equals("Volleyball", ignoreCase = true)
                val rightGroupGap = if (isLongSport) 4 else 12
                Row(
                    modifier = Modifier.mGap(rightGroupGap),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Theme toggle — News only for now (sits first so it's
                    // present even alongside a page action slot).
                    if (showThemeButton) MobileThemeButton()
                    if (actionSlot != null) {
                        actionSlot()
                    } else {
                        MobileNotificationButton(
                            badgeCount = when (headerAction) {
                                MobileHeaderAction.Activity -> activityCount
                                else -> notificationCount
                            },
                            action = headerAction,
                            onClick = {
                                showSportDropdown = false
                                when (headerAction) {
                                    // The heart (Likes) opens the full-screen
                                    // post-detail sheet (Figma 8282:171340).
                                    MobileHeaderAction.Likes -> showLikesSheet = true
                                    MobileHeaderAction.Notification -> showNotificationsSheet = true
                                    MobileHeaderAction.Activity -> showActivitySheet = true
                                }
                            }
                        )
                    }
                    when (trailing) {
                        MobileHeaderTrailing.Profile -> MobileProfileButton(
                            avatarUrl = avatarUrl,
                            onClick = {
                                showSportDropdown = false
                                showProfileSheet = true
                            }
                        )
                        MobileHeaderTrailing.Chat -> MobileChatButton(
                            onClick = {
                                showSportDropdown = false
                                ctx.router.navigateTo("/chat")
                            }
                        )
                        MobileHeaderTrailing.None -> {}
                    }
                }
            } else {
                // Full reload after login so all pages re-read auth state
                // from storage (the `remember { Storage.contains(...) }`
                // initialisers in Arena/Feed/People only run once per mount).
                val onLoggedIn: () -> Unit = {
                    kotlinx.browser.window.location.reload()
                }
                Row(
                    modifier = Modifier.mGap(6),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // A page-provided action (e.g. the News mode toggle)
                    // replaces the auth buttons for guests too — both don't
                    // fit next to the centred sport pill on a phone.
                    if (actionSlot != null) {
                        actionSlot()
                    } else {
                        MobileGoogleButton(onLoggedIn = onLoggedIn)
                        MobileAppleButton(onLoggedIn = onLoggedIn)
                    }
                }
            }
        }

        // Centre: either the page title text (Figma 8285:172272) or the
        // sport selector pill, pinned to the EXACT viewport horizontal centre
        // via left:50% + translateX(-50%). z-index lifts the wrapper above
        // sibling rows so the open sport dropdown isn't trapped by the
        // transform-induced stacking context.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "50%")
                property("top", "50%")
                property("transform", "translate(-50%, -50%)")
                property("z-index", "1100")
            }
        ) {
            if (pageTitle != null) {
                SpanText(
                    pageTitle,
                    modifier = Modifier
                        .fontSize(24.px)
                        .fontWeight(500)
                        .lineHeight(32.px)
                        .letterSpacing(0.24.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("font-family", "'Urbanist', sans-serif")
                            property("white-space", "nowrap")
                        }
                )
            } else {
                MobileSportSelector(
                    selectedSportName = selectedSportName,
                    sportsList = sportsList,
                    showDropdown = showSportDropdown,
                    onToggle = { showSportDropdown = !showSportDropdown },
                    onSelect = { sport ->
                        Storage.save(StorageKeys.SELECTED_SPORT_ID, sport.id)
                        Storage.save(StorageKeys.SELECTED_SPORT_NAME, sport.name)
                        if (!sport.name.equals("other", ignoreCase = true)) {
                            Storage.save(StorageKeys.SELECTED_OTHER_SPORT, "")
                            selectedOtherSport = null
                        }
                        selectedSportName = sport.name
                        onSportChanged(sport.name)
                        showSportDropdown = false
                    },
                    selectedOtherSport = selectedOtherSport,
                    onOtherSportSelected = { name ->
                        Storage.save(StorageKeys.SELECTED_OTHER_SPORT, name ?: "")
                        selectedOtherSport = name
                    }
                )
            }
        }
    }

    // Full-screen "Interactions" sheet (Figma 8289:3249) — overlays the page
    // when the heart action is tapped. position:fixed inside BottomSheet, so
    // mounting it here is enough to cover the whole viewport.
    if (showLikesSheet) {
        com.arena_web.ui.components.overlay.MobileInteractionsSheet(
            sportName = selectedSportName,
            onDismiss = { showLikesSheet = false }
        )
    }

    // Phone counterparts of the desktop header dropdowns — see MobileHeaderSheets.
    if (showNotificationsSheet) {
        MobileNotificationsSheet(onDismiss = { showNotificationsSheet = false })
    }
    if (showActivitySheet) {
        MobileActivitySheet(onDismiss = { showActivitySheet = false })
    }
    if (showProfileSheet) {
        MobileProfileSheet(onDismiss = { showProfileSheet = false })
    }
}

// =============================================================================
// Sport Selector (matches Figma design)
// =============================================================================

@Composable
private fun MobileSportSelector(
    selectedSportName: String,
    sportsList: List<SportDto>,
    showDropdown: Boolean,
    onToggle: () -> Unit,
    onSelect: (SportDto) -> Unit,
    selectedOtherSport: String? = null,
    onOtherSportSelected: (String?) -> Unit = {},
) {
    val t = LocalAppStrings.current
    val sportColors = SportColors.forSport(selectedSportName.lowercase())

    // Stable DOM id so document-level mousedown can detect clicks outside
    // both the trigger pill and the menu. Same trick the Arena-mobile
    // location dropdown uses (ArenaContentMobile).
    val rootId = remember { "mobile-sport-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(showDropdown) {
        if (!showDropdown) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(rootId)
            if (target != null && root != null && !root.contains(target)) {
                onToggle()
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(rootId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .mHeight(44)
                .mBorderRadius(26)
                .mPadding(8, 18)
                .mGap(12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    // Figma 10032:208784 — Interactive/Secondary fill +
                    // Button/border/gradient/primary stroke. No backdrop-filter
                    // on this pill, so the border-box trick is fine (mirrors
                    // the desktop SportSelectorPill).
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.headerSportLabel,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(500)
                    .mLineHeight(18)
                    .mLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Row(
                modifier = Modifier.mGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    selectedSportName.replaceFirstChar { it.uppercase() },
                    modifier = Modifier
                        .mFontSize(16)
                        .fontWeight(500)
                        .mLineHeight(18)
                        .mLetterSpacing(0.2)
                        .color(sportColors.metricsPrimary)
                )
                // Figma renders chevron-down inside a 16px slot at its natural
                // 15.75 × 9 aspect. Raw px overrides Icon's default sWidth/sHeight,
                // which would be ≈ 6×3.6 px on mobile (--scale ≈ 0.4 below 768).
                Icon(
                    src = AppIcons.ChevronDown,
                    modifier = Modifier.mWidth(12).mHeight(7)
                )
            }
        }

        if (showDropdown && sportsList.isNotEmpty()) {
            // Shared with desktop AppHeader's SportSelectorPill — same panel,
            // same icon set, same colour rules. See `SportDropdownPanel`.
            SportDropdownPanel(
                selectedSportName = selectedSportName,
                sportsList = sportsList,
                onSelect = onSelect,
                selectedOtherSport = selectedOtherSport,
                onSelectOtherSport = onOtherSportSelected,
                modifier = Modifier
                    .position(Position.Absolute)
                    .mTop(50)
                    .zIndex(1100)
                    .styleModifier {
                        property("left", "50%")
                        property("transform", "translateX(-50%)")
                    }
            )
        }
    }
}

// =============================================================================
// Notification button with unread badge — Figma 8398:85395.
// =============================================================================

@Composable
private fun MobileNotificationButton(
    badgeCount: Int,
    action: MobileHeaderAction = MobileHeaderAction.Notification,
    onClick: () -> Unit = {}
) {
    val sportColors = LocalSportColors.current
    val iconSrc = when (action) {
        MobileHeaderAction.Notification -> AppIcons.Notifications
        MobileHeaderAction.Likes -> AppIcons.Heart
        MobileHeaderAction.Activity -> AppIcons.Pulse
    }
    // Likes uses the loss/red accent (Figma 8285:375086); notifications and
    // activity use the active sport's primary colour.
    val badgeBg = when (action) {
        MobileHeaderAction.Likes -> "#FF4242"
        else -> sportColors.primary.toString()
    }

    Box(modifier = Modifier.position(Position.Relative)) {
        Box(
            modifier = Modifier
                .mSize(36)
                .mBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onClick() }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(src = iconSrc, modifier = Modifier.mSize(16))
        }

        if (badgeCount > 0) {
            Box(
                modifier = Modifier
                    .mSize(12)
                    .mBorderRadius(100)
                    .styleModifier { property("background-color", badgeBg) }
                    .position(Position.Absolute)
                    .top(0.px)
                    .right(0.px)
                    .overflow(Overflow.Hidden),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    badgeCount.toString(),
                    modifier = Modifier
                        .mFontSize(8)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier {
                            property("font-family", "'Sora', sans-serif")
                            property("line-height", "1")
                            property("transform", "translateY(0.5px)")
                        }
                )
            }
        }
    }
}

// =============================================================================
// Profile Button — avatar that opens the profile bottom sheet (the phone
// counterpart of the desktop ProfilePill dropdown, see MobileHeaderSheets).
// =============================================================================

@Composable
private fun MobileProfileButton(
    avatarUrl: String?,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .overflow(Overflow.Hidden)
            .backgroundColor(AppColors.Text.Primary)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl != null) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                        "onerror=\"this.style.display='none'\" " +
                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                    onDispose {}
                }
                style { property("width", "100%"); property("height", "100%") }
            })
        } else {
            Icon(src = AppIcons.Profile, modifier = Modifier.mSize(18))
        }
    }
}

// =============================================================================
// Chat Button — Arena's trailing action; routes straight to the Chat page.
// Same 36-px circle chrome as MobileNotificationButton.
// =============================================================================

@Composable
private fun MobileChatButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Chat, modifier = Modifier.mSize(16))
    }
}

// Light/dark theme toggle — same 36-px glass chrome as the other mobile
// header buttons. Shows the glyph of the theme it switches TO (golden-less
// monochrome so the icon filter tints it to match its neighbours).
@Composable
private fun MobileThemeButton() {
    val isLight = com.arena_web.ui.theme.ThemeController.isLight
    val sunSvg =
        "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none' " +
            "stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>" +
            "<circle cx='12' cy='12' r='4.2'/>" +
            "<path d='M12 2.6v2.4M12 19v2.4M21.4 12H19M5 12H2.6" +
            "M18.7 5.3l-1.7 1.7M7 17l-1.7 1.7M18.7 18.7L17 17M7 7L5.3 5.3'/></svg>"
    val moonSvg =
        "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none'>" +
            "<path d='M20 14.5A8 8 0 0 1 9.5 4a0.6 0.6 0 0 0-0.8-0.8 8.8 8.8 0 1 0 12.1 12.1 " +
            "0.6 0.6 0 0 0-0.8-0.8z' fill='currentColor'/></svg>"
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { com.arena_web.ui.theme.ThemeController.toggle() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        androidx.compose.runtime.key(isLight) {
            org.jetbrains.compose.web.dom.Div(attrs = Modifier
                .mSize(18)
                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                .styleModifier { property("line-height", "0") }
                .toAttrs {
                    ref { el ->
                        el.innerHTML = if (isLight) moonSvg else sunSvg
                        onDispose { }
                    }
                })
        }
    }
}

// =============================================================================
// Mobile Auth Buttons (compact)
// =============================================================================

@Composable
private fun MobileGoogleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(18)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.triggerGoogleSignIn(
                        onSuccess = { googleUser ->
                            val lang = OAuthHelper.localeToLanguageName(googleUser.locale)
                            loginViewModel.loginWithOAuth(
                                provider = "google",
                                providerId = googleUser.sub,
                                email = googleUser.email,
                                fullName = googleUser.name,
                                avatarUrl = googleUser.picture,
                                idToken = googleUser.idToken,
                                languages = lang?.let { listOf(it) },
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Google sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Google, modifier = Modifier.mSize(16))
    }
}

@Composable
private fun MobileAppleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(18)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.signInWithApple(
                        onSuccess = { email, name, providerId, idToken ->
                            loginViewModel.loginWithOAuth(
                                provider = "apple",
                                providerId = providerId,
                                email = email,
                                fullName = name,
                                idToken = idToken,
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Apple sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = AppIcons.Apple, modifier = Modifier.mSize(14))
    }
}

